import React, { createContext, forwardRef, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Popper from '@mui/material/Popper';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as R from 'ramda';
import { VariableSizeList } from 'react-window';

/* 
  react-window list virtualization adapted from example in MUI docs
  https://mui.com/material-ui/react-autocomplete/#virtualization
*/
const LISTBOX_PADDING = 8;

const renderRow = ({ data, index, style }) => {
  const dataset = data[index];
  const inlineStyle = {
    ...style,
    backgroundColor: dataset[2] ? 'primary.lighter' : 'transparent',
    top: style.top + LISTBOX_PADDING,
  };

  return (
    <Typography component="li" noWrap sx={inlineStyle} variant="body1" {...dataset[0]}>
      {dataset[1].customerName}
    </Typography>
  );
};

/* Set up context to pass values to onChange */
const OuterElementContext = createContext({});

const OuterElementType = forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return (
    <Box {...outerProps} {...props} ref={ref} sx={{ padding: theme => theme.spacing(1, 0) }} />
  );
});

const useResetCache = data => {
  const ref = useRef();

  useEffect(() => {
    if (ref.current !== null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);

  return ref;
};

/* Adapter for react-window */
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
  // eslint-disable-next-line react/prop-types
  const { children, ...other } = props;
  const ITEM_SIZE = 32;
  const MAX_ITEM_LENGTH = 5;

  const itemData = [];

  // eslint-disable-next-line react/prop-types
  children.forEach(item => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const getHeight = () => {
    if (itemData.length > MAX_ITEM_LENGTH) return MAX_ITEM_LENGTH * ITEM_SIZE;

    return itemData.length * ITEM_SIZE;
  };

  const gridRef = useResetCache(itemData.length);

  return (
    <Box ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          height={getHeight() + 2 * LISTBOX_PADDING}
          innerElementType="ul"
          itemCount={itemData.length}
          itemData={itemData}
          itemSize={() => ITEM_SIZE}
          outerElementType={OuterElementType}
          overscanCount={5}
          ref={gridRef}
          width="100%"
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </Box>
  );
});

ListboxComponent.propType = {
  props: { children: PropTypes.node },
};

/* 
  Define Popper for Autocomplete outside component to prevent jumping to top of 
  options list on select
*/
const PopperComponent = popperProps => (
  <Popper
    {...popperProps}
    placement="bottom-start"
    sx={{
      '& ul': {
        margin: 0,
        padding: 0,
      },
      '.MuiAutocomplete-option': {
        '&.Mui-focused': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        cursor: 'pointer',
        padding: theme => theme.spacing('6px', 2),
      },
    }}
  />
);

// eslint-disable-next-line react/prop-types
const VirtualizedAutocomplete = ({ handleChange, optionIdName, optionLabel, options, value }) => {
  return (
    <Autocomplete
      disableCloseOnSelect
      getOptionLabel={R.prop(optionLabel)}
      isOptionEqualToValue={R.eqProps(optionIdName)}
      multiple
      onChange={(_, val) => {
        handleChange(val);
      }}
      options={options}
      renderInput={rProps => (
        <TextField
          {...rProps}
          label="Customers"
          slotProps={{
            input: {
              ...rProps.InputProps,
              endAdornment: <SearchIcon fontSize="small" />,
            },
          }}
        />
      )}
      renderOption={(props, option, { selected }) => [props, option, selected]}
      renderTags={(tags, getProps) =>
        tags?.map((option, index) => (
          <Chip
            {...getProps({ index })}
            label={option.customerName}
            sx={{
              borderColor: 'secondary.main',
              borderRadius: 25,
              boxSizing: 'border-box',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '45%',
            }}
            variant="outlined"
          />
        ))
      }
      slotProps={{
        listbox: {
          component: ListboxComponent,
        },
      }}
      slots={{
        popper: PopperComponent,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          paddingRight: '8px !important',
        },
      }}
      value={value}
    />
  );
};

export default VirtualizedAutocomplete;

VirtualizedAutocomplete.propType = {
  handleChange: PropTypes.func.isRequired,
  optionIdName: PropTypes.string.isRequired,
  optionLabel: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape).isRequired,
  value: PropTypes.arrayOf(PropTypes.shape).isRequired,
};
